import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, FolderOpen } from 'lucide-react'
import { workspaceApi, projectApi } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/ui/empty-state'

export default function WorkspaceProjectsPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const currentUserId = useAuthStore((s) => s.user?.id)

  const { data: members } = useQuery({
    queryKey: ['workspace', slug, 'members'],
    queryFn: () => workspaceApi.getMembers(slug!),
    enabled: !!slug,
  })

  const {
    data: projects,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['workspace', slug, 'projects'],
    queryFn: () => projectApi.getProjects(slug!),
    enabled: !!slug,
  })

  const myMember = members?.find((m) => m.user_id === currentUserId)
  const canCreate = myMember?.role !== 'viewer'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Project</h1>
          <p className="text-sm text-muted-foreground">Semua project dalam workspace ini.</p>
        </div>
        {canCreate && (
          <button
            onClick={() => navigate(`/workspaces/${slug}/projects/new`)}
            className="hidden rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover md:block"
          >
            + Buat Project
          </button>
        )}
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-sm text-destructive">Gagal memuat project. Coba refresh halaman.</p>
      )}

      {/* Project grid */}
      {!isLoading && !error && projects && (
        <>
          {projects.length === 0 ? (
            <EmptyState
              title="Belum ada project di workspace ini."
              description="Mulai dengan membuat project pertama."
              action={
                canCreate ? (
                  <Link
                    to={`/workspaces/${slug}/projects/new`}
                    className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover"
                  >
                    <Plus className="h-4 w-4" />
                    Buat Project Pertama
                  </Link>
                ) : undefined
              }
            />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => navigate(`/workspaces/${slug}/projects/${project.id}`)}
                  className="group flex flex-col items-start gap-2 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:bg-secondary"
                >
                  <div className="flex w-full items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FolderOpen className="h-4 w-4 shrink-0 text-primary" />
                      <span className="truncate font-semibold text-sm">{project.name}</span>
                    </div>
                  </div>
                  {project.description && (
                    <p className="line-clamp-2 text-xs text-muted-foreground">
                      {project.description}
                    </p>
                  )}
                  <span className="text-xs text-muted-foreground/60 font-mono">
                    /{project.slug}
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Mobile FAB */}
      {canCreate && (
        <Link
          to={`/workspaces/${slug}/projects/new`}
          aria-label="Buat project baru"
          className="fixed bottom-6 right-6 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary-hover md:hidden"
        >
          <Plus className="h-6 w-6" />
        </Link>
      )}
    </div>
  )
}
